import React from "react";
import { Checkbox, Modal, Typography } from "@arco-design/web-react";

const { Text } = Typography;

// The server shrinks JPEG and PNG uploads to fit this, so they load fast on
// forms and in emails (see internal/services/file/web_image.go).
export const WEB_MAX_WIDTH = 1200;
export const WEB_MAX_HEIGHT = 2400;

const SHRUNK_TYPES = ["image/jpeg", "image/png"];

// imageSize reads an image file's width and height in the browser, or null if
// it isn't a JPEG or PNG or can't be read.
export function imageSize(file) {
  return new Promise((resolve) => {
    if (!file || !SHRUNK_TYPES.includes(file.type)) return resolve(null);
    let url;
    try {
      url = URL.createObjectURL(file);
    } catch (e) {
      return resolve(null);
    }
    const img = new window.Image();
    const done = (size) => {
      URL.revokeObjectURL(url);
      resolve(size);
    };
    img.onload = () => done({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => done(null);
    img.src = url;
  });
}

// shrunkSize is the size the server will shrink an image to, or null if it
// already fits.
export function shrunkSize(size) {
  if (!size || !size.width || !size.height) return null;
  const scale = Math.min(1, WEB_MAX_WIDTH / size.width, WEB_MAX_HEIGHT / size.height);
  if (scale >= 1) return null;
  return { width: Math.max(1, Math.round(size.width * scale)), height: Math.max(1, Math.round(size.height * scale)) };
}

// KeepChoice holds the checkbox's state: Modal.confirm content is drawn
// outside the page's components.
function KeepChoice({ large, onChange }) {
  const [keep, setKeep] = React.useState(false);
  return (
    <div>
      <p style={{ marginTop: 0 }}>
        {large.length === 1 ? "This image is" : "These images are"} bigger than forms and emails need,
        so {large.length === 1 ? "it" : "they"}'ll be shrunk to load fast:
      </p>
      <ul style={{ paddingLeft: 18 }}>
        {large.map(({ file, from, to }) => (
          <li key={file.name}>
            <Text>{file.name}</Text>: {from.width} × {from.height} → {to.width} × {to.height}
          </li>
        ))}
      </ul>
      <Checkbox checked={keep} onChange={(v) => { setKeep(v); onChange(v); }}>
        Also keep the full-size original (for example, to give away as a download)
      </Checkbox>
    </div>
  );
}

// confirmUpload tells the user which images will be shrunk and asks whether
// to keep their full-size originals. Resolves { keepOriginal }, or null if
// they cancel. Uploads with no large images go ahead without asking.
export async function confirmUpload(files) {
  const sizes = await Promise.all(files.map(imageSize));
  const large = files
    .map((file, i) => ({ file, from: sizes[i], to: shrunkSize(sizes[i]) }))
    .filter((f) => f.to);
  if (large.length === 0) return { keepOriginal: false };

  return new Promise((resolve) => {
    let keep = false;
    Modal.confirm({
      title: large.length === 1 ? "Large image" : "Large images",
      content: <KeepChoice large={large} onChange={(v) => { keep = v; }} />,
      okText: "Upload",
      onOk: () => resolve({ keepOriginal: keep }),
      onCancel: () => resolve(null),
    });
  });
}

// uploadFields is the extra form data for an upload.
export const uploadFields = (choice) => ({ keep_original: choice && choice.keepOriginal ? "true" : "false" });
